import React, { useState, useEffect } from 'react';
import { AdminDashboard } from './AdminDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon, MapPin } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPartner, setEditingPartner] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    logo: '',
    website: ''
  });

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const data = await adminApi.getPartners();
      setPartners(data);
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner.id);
    setFormData({
      name: partner.name,
      location: partner.location,
      logo: partner.logo || '',
      website: partner.website || ''
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingPartner(null);
    setFormData({
      name: '',
      location: '',
      logo: '',
      website: ''
    });
  };

  const handleCancel = () => {
    setEditingPartner(null);
    setIsCreating(false);
    setFormData({ name: '', location: '', logo: '', website: '' });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const result = await adminApi.uploadPartnerLogo(file);
      setFormData({ ...formData, logo: result.path });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.name || !formData.location) {
        toast.error('Name and location are required');
        return;
      }

      if (isCreating) {
        await adminApi.createPartner(formData);
        toast.success('Partner created successfully');
      } else {
        await adminApi.updatePartner(editingPartner, formData);
        toast.success('Partner updated successfully');
      }

      loadPartners();
      handleCancel();
    } catch (error) {
      toast.error('Failed to save partner');
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;

    try {
      await adminApi.deletePartner(partnerId);
      toast.success('Partner deleted successfully');
      loadPartners();
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  if (loading) {
    return (
      <AdminDashboard>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Loading partners...</p>
        </div>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Partners</h1>
            <p className="text-slate-600 mt-1">Manage your company partners</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-[#1E4E9E] to-[#00B050] hover:from-[#00B050] hover:to-[#1E4E9E] text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Partner
          </Button>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingPartner) && (
          <Card className="border-2 border-[#1E4E9E]">
            <CardHeader>
              <CardTitle>{isCreating ? 'Create New Partner' : 'Edit Partner'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Partner name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Location *</label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="City, Country"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Website URL</label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://partner-website.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Logo</label>
                <div className="flex items-center space-x-4">
                  <label className="cursor-pointer">
                    <div className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-[#1E4E9E] transition-colors">
                      <Upload className="w-5 h-5 text-slate-500" />
                      <span className="text-sm text-slate-600">
                        {uploading ? 'Uploading...' : 'Upload Logo'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  {formData.logo && (
                    <div className="flex items-center space-x-2">
                      <ImageIcon className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-600">Logo uploaded</span>
                    </div>
                  )}
                </div>
                {formData.logo && (
                  <img
                    src={`${BACKEND_URL}${formData.logo}`}
                    alt="Logo Preview"
                    className="mt-4 w-32 h-32 object-contain rounded-lg border bg-white p-2"
                  />
                )}
              </div>

              <div className="flex space-x-3">
                <Button onClick={handleSave} className="bg-[#00B050] hover:bg-[#00B050]/90">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={handleCancel} variant="outline">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Partners List */}
        <div className="grid md:grid-cols-3 gap-6">
          {partners.map((partner) => (
            <Card key={partner.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{partner.name}</CardTitle>
                    <div className="flex items-center text-sm text-slate-600 mt-1">
                      <MapPin className="w-4 h-4 mr-1" />
                      {partner.location}
                    </div>
                    {partner.website && (
                      <a 
                        href={partner.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#1E4E9E] hover:underline mt-1 block"
                      >
                        {partner.website}
                      </a>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(partner)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(partner.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {partner.logo ? (
                  <div className="flex items-center justify-center h-32 bg-slate-50 rounded-lg">
                    <img
                      src={`${BACKEND_URL}${partner.logo}`}
                      alt={partner.name}
                      className="max-h-24 max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 bg-slate-50 rounded-lg">
                    <p className="text-slate-400 text-sm">No logo</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {partners.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500">No partners yet. Create your first partner!</p>
          </div>
        )}
      </div>
    </AdminDashboard>
  );
};
